<template>
  <transition name="fading">
    <div class="overlay" v-if="isActive">
      <div class="clinic-modal" v-if="loaded">
        <div class="modal-header">
          <div>
            <p class="page-kicker">Location Setup</p>
            <h2>Create clinic / hospital</h2>
            <p>Add a service location and attach it to the right organization.</p>
          </div>
          <button class="icon-close" @click="close_modal">
            <i class="fa-regular fa-circle-xmark"></i>
          </button>
        </div>

        <div v-if="validationError" class="alert-message">
          <i class="fas fa-exclamation-circle"></i> {{ validationError }}
        </div>

        <div class="form-grid">
          <div class="form-section full-width" v-if="authStore.userRole === 'admin'">
            <label class="field-label">Organization <span class="required">*</span></label>
            <select class="ui-select" v-model="clinic_data.OrganizationUuid">
              <option value="">Choose organization</option>
              <option v-for="org in orgSelection" :key="org.uuid" :value="org.uuid">
                {{ org.name }}
              </option>
            </select>
            <span class="field-hint">Every clinic or hospital location belongs to an organization.</span>
          </div>

          <div class="form-section full-width">
            <label class="field-label">Clinic / hospital name <span class="required">*</span></label>
            <input class="ui-input" v-model="clinic_data.clinic_name" placeholder="e.g. Dr Mata Bangsar">
          </div>

          <div class="form-section full-width">
            <label class="field-label">Address <span class="optional-pill">Optional</span></label>
            <textarea class="ui-textarea" v-model="clinic_data.address" placeholder="Street address"></textarea>
          </div>

          <div class="form-section">
            <label class="field-label">City</label>
            <input class="ui-input" v-model="clinic_data.city" placeholder="e.g. Kuala Lumpur">
          </div>

          <div class="form-section">
            <label class="field-label">State</label>
            <select class="ui-select" v-model="clinic_data.state">
              <option value="">Select state</option>
              <option :value="state" v-for="state in states" :key="state">{{ state }}</option>
            </select>
          </div>

          <div class="form-section">
            <label class="field-label">Postal code</label>
            <input class="ui-input" v-model="clinic_data.postal_code" placeholder="e.g. 50450">
          </div>
        </div>

        <div class="ui-actions">
          <button class="ui-button secondary" @click="close_modal()">Cancel</button>
          <button class="ui-button primary" @click="submit()">
            <i class="fa-solid fa-plus"></i> Create location
          </button>
        </div>
      </div>
      <Loading v-else />
    </div>
  </transition>
</template>

<script>
import { registerClinics } from "@/services/clinicServices"
import { getAllOrgArray } from "@/services/orgServices"
import Loading from "@/components/Loading.vue";
import StatesJSON from "@/constants/states"
import { useAuthStore } from '@/stores/authStore'

export default{
  components: {
    Loading,
  },

  props: {
    isActive: {
      type: Boolean,
    }
  },

  mounted() {
    this.getOrganizations();
  },

  data() {
    return {
      authStore: useAuthStore(),
      loaded: true,
      clinic_data: {
        clinic_name: "",
        address: "",
        city: "",
        state: "",
        country: "Malaysia",
        postal_code: "",
        latitude: "",
        longitude: "",
        OrganizationUuid: ""
      },
      states: StatesJSON,
      orgSelection: [],
      validationError: "",
    }
  },
  methods: {
    close_modal(){
      this.validationError = "";
      this.$emit('modal-close');
    },

    async getOrganizations() {
      if (this.authStore.userRole === 'admin') {
        this.orgSelection = await getAllOrgArray();
      }
    },

    validateForm() {
      if (this.authStore.userRole === 'admin' && !this.clinic_data.OrganizationUuid) {
        this.validationError = "Choose an organization for this location.";
        return false;
      }
      if (!this.clinic_data.clinic_name.trim()) {
        this.validationError = "Clinic or hospital name is required.";
        return false;
      }
      this.validationError = "";
      return true;
    },

    async submit(){
      if (!this.validateForm()) {
        return;
      }

      this.loaded = false;
      try {
        const response = await registerClinics(this.clinic_data);
        if(response){
          this.resetData();
          this.$emit('modal-submit');
        }
      } catch (error) {
        console.log(error);
        this.validationError = "Unable to create location. Please try again.";
      } finally {
        this.loaded = true;
      }
    },

    resetData(){
      this.clinic_data = {
        clinic_name: "",
        address: "",
        city: "",
        state: "",
        country: "Malaysia",
        postal_code: "",
        latitude: "",
        longitude: "",
        OrganizationUuid: ""
      }
    },
  }
}
</script>

<style scoped>
.overlay {
  position: fixed;
  inset: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
  background: rgba(23, 18, 37, 0.42);
  backdrop-filter: blur(8px);
  z-index: 1000;
}

.clinic-modal {
  width: min(760px, 100%);
  max-height: 92vh;
  overflow-y: auto;
  padding: 26px;
  border: 1px solid var(--ui-border);
  border-radius: 24px;
  background: #fff;
  box-shadow: 0 24px 90px rgba(23, 18, 37, 0.24);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  gap: 18px;
  margin-bottom: 20px;
}

.modal-header h2 {
  margin: 0;
  color: var(--ui-text);
  font-weight: 800;
  letter-spacing: -0.03em;
}

.modal-header p:last-child {
  margin: 6px 0 0;
  color: var(--ui-muted);
}

.icon-close {
  width: 38px;
  height: 38px;
  border: 1px solid var(--ui-border);
  border-radius: 999px;
  background: #fff;
  color: var(--primary-dark);
}

.fading-enter-active,
.fading-leave-active {
  transition: opacity 0.2s;
}

.fading-enter-from,
.fading-leave-to {
  opacity: 0;
}
</style>
